import {
  AfterViewInit,
  Component,
  ChangeDetectorRef,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ToastService } from '@black/ui';
import { AuthService } from '../../services/auth.service';
import { TenantService } from '../../services/tenant.service';
import { isBackendApiRequest } from '../../util/helper.utils';
import {
  VideoSourceResolverService,
  PlaybackSourceDescriptor,
} from '../../services/video-source-resolver.service';

@Component({
  selector: 'black-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  standalone: true,
  host: {
    class: 'w-full h-full block',
  },
})
export class VideoPlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private tenantService = inject(TenantService);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  private sanitizer = inject(DomSanitizer);
  private sourceResolver = inject(VideoSourceResolverService);

  private hostRef = inject(ElementRef);

  @ViewChild('videoPlayer') videoElementRef: ElementRef<HTMLVideoElement> | undefined;
  @ViewChild('videoContainer') videoContainerRef: ElementRef<HTMLDivElement> | undefined;

  @Input() videoURL = '';
  @Input() resourceId = '';
  @Input() autoplay = false;

  get videoElement(): HTMLVideoElement | undefined {
    return this.videoElementRef?.nativeElement;
  }

  get videoContainerElement(): HTMLDivElement | undefined {
    return this.videoContainerRef?.nativeElement;
  }

  isLoading = true;
  embedUrl: SafeResourceUrl | null = null;
  pdfUrl: SafeResourceUrl | null = null;
  rawPdfUrl: string | null = null;
  linkUrl: string | null = null;
  currentResolvedSource: PlaybackSourceDescriptor | null = null;

  private isEmbedPlaying = true;
  private isViewInitialized = false;
  private player: any;
  private ui: any;
  private resolveSub: Subscription | null = null;

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      return;
    }

    if (event.code === 'Space' || event.key === 'k' || event.key === 'K') {
      event.preventDefault();
      this.togglePlayPause();
    }
  }

  togglePlayPause(): void {
    if (this.embedUrl) {
      const iframe =
        (this.hostRef.nativeElement as HTMLElement).querySelector('iframe');
      if (iframe?.contentWindow) {
        this.isEmbedPlaying = !this.isEmbedPlaying;
        const rawUrl = this.currentResolvedSource?.src || this.videoURL;
        const isVimeo = rawUrl.includes('vimeo.com');
        const payload = isVimeo
          ? { method: this.isEmbedPlaying ? 'play' : 'pause' }
          : { event: 'command', func: this.isEmbedPlaying ? 'playVideo' : 'pauseVideo', args: [] };
        iframe.contentWindow.postMessage(JSON.stringify(payload), '*');
      }
    } else if (this.videoElement) {
      this.videoElement.paused ? this.videoElement.play() : this.videoElement.pause();
    }
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    if (!isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
      return;
    }

    this.resolveAndPlay();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!isPlatformBrowser(this.platformId) || !this.isViewInitialized) {
      return;
    }

    if (changes['videoURL'] || changes['resourceId']) {
      this.resolveAndPlay();
    }
  }

  private resolveAndPlay(): void {
    this.resolveSub?.unsubscribe();

    // Immediately stop previous media to avoid audio bleed during resolution
    if (this.videoElement) {
      this.videoElement.pause();
    }
    if (this.player) {
      this.player.unload().catch(() => {});
    }
    this.embedUrl = null;
    this.pdfUrl = null;
    this.rawPdfUrl = null;
    this.linkUrl = null;
    this.isLoading = true;
    this.cdr.markForCheck();

    if (this.resourceId) {
      this.resolveSub = this.sourceResolver
        .resolveResource(this.resourceId)
        .subscribe((descriptor) => {
          this.applySourceDescriptor(descriptor);
        });
    } else if (this.videoURL) {
      const descriptor = this.sourceResolver.resolveUrl(this.videoURL);
      this.applySourceDescriptor(descriptor);
    } else {
      this.applySourceDescriptor({ engine: 'shaka', src: '' });
    }
  }

  private applySourceDescriptor(descriptor: PlaybackSourceDescriptor): void {
    this.currentResolvedSource = descriptor;
    this.embedUrl = null;
    this.pdfUrl = null;
    this.rawPdfUrl = null;
    this.linkUrl = null;

    if (!descriptor.src || descriptor.engine !== 'shaka') {
      this.videoElement?.pause();
      this.player?.unload().catch(() => {});
    }

    if (!descriptor.src) {
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    if (descriptor.engine === 'pdf') {
      this.rawPdfUrl = descriptor.src;
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(descriptor.src);
      this.isLoading = false;
      this.cdr.detectChanges();
    } else if (descriptor.engine === 'iframe') {
      this.embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(descriptor.src);
      this.isEmbedPlaying = true;
      this.isLoading = false;
      this.cdr.detectChanges();
    } else if (descriptor.engine === 'link') {
      this.linkUrl = descriptor.src;
      this.isLoading = false;
      this.cdr.detectChanges();
    } else {
      // Shaka video playback
      this.cdr.detectChanges();
      if (this.player) {
        this.loadVideo(descriptor);
      } else {
        this.initPlayer(descriptor);
      }
    }
  }

  private destroyPlayer(): void {
    if (this.ui) {
      this.ui.destroy();
      this.ui = null;
    }
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
    if (this.videoElement) {
      this.videoElement.pause();
      this.videoElement.removeAttribute('src');
      this.videoElement.load();
    }
  }

  private loadVideo(descriptor?: PlaybackSourceDescriptor) {
    const source = descriptor || this.currentResolvedSource;
    const videoUrl = source?.src || this.videoURL;
    if (!videoUrl || !this.player) {
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.player
      .load(videoUrl, undefined, source?.mimeType)
      .then(() => {
        this.videoElement?.play().catch(() => {});
        this.isLoading = false;
        this.cdr.markForCheck();
      })
      .catch(() => {
        this.toast.showToast('Failed to load the video file', 'error');
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  private async initPlayer(descriptor?: PlaybackSourceDescriptor) {
    if (this.embedUrl || this.pdfUrl) {
      return;
    }

    if (this.player) {
      this.loadVideo(descriptor);
      return;
    }

    if (!this.videoElement) {
      this.toast.showToast('Video element not found!', 'error');
      this.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    try {
      const shaka = (await import('shaka-player/dist/shaka-player.ui.js')).default;
      shaka.polyfill.installAll();

      if (!shaka.Player.isBrowserSupported()) {
        this.toast.showToast('Browser not supported!', 'error');
        this.isLoading = false;
        this.cdr.markForCheck();
        return;
      }

      this.player = new shaka.Player(this.videoElement);

      // Configure network filters to add JWT and Tenant headers to backend requests
      const networkingEngine = this.player.getNetworkingEngine();
      if (networkingEngine) {
        networkingEngine.registerRequestFilter(
          async (type: any, request: any) => {
            const isBackend = request.uris?.some((uri: string) =>
              isBackendApiRequest(uri),
            );
            if (isBackend) {
              const authToken = await this.authService.getToken();
              if (authToken) {
                request.headers['Authorization'] = `Bearer ${authToken}`;
              }

              const slug = this.tenantService.getTenantSlug();
              const tenantId = this.tenantService.getTenantId();

              if (slug) {
                request.headers['X-Tenant-Slug'] = slug;
              }
              if (tenantId) {
                request.headers['X-Tenant-ID'] = tenantId;
              }
            }
          },
        );
      }

      if (this.videoContainerElement && this.videoElement) {
        this.ui = new shaka.ui.Overlay(
          this.player,
          this.videoContainerElement as HTMLElement,
          this.videoElement,
        );

        const config_blue = {
          seekBarColors: {
            base: 'rgba(66, 133, 244, 0.35)',
            buffered: 'rgba(66, 133, 244, 0.6)',
            played: 'rgba(66, 133, 244, 0.8)',
          },
          volumeBarColors: {
            base: 'rgba(66, 133, 244, 0.8)',
            level: 'rgb(66, 133, 244)',
          },
        };

        this.ui.configure(config_blue);
      }

      this.player.addEventListener('buffering', (event: any) => {
        this.isLoading = Boolean(event.buffering);
        this.cdr.markForCheck();
      });

      this.loadVideo(descriptor);
    } catch (error) {
      this.toast.showToast('Failed to initialize video player', 'error');
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.resolveSub?.unsubscribe();
    this.destroyPlayer();
  }
}
